import {IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {IDotRezBookingSession} from "../../../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";
import {BookingModel} from "../booking.model";
import {IServiceFactory} from "../../../service-factory.interface";
import {
    IFinalizePspOrderRequest,
    ILoadPaymentMethodsOptions,
    IOnPayBookingFailedParams,
    IPaymentHandlerViewModel,
    IStartBookingPaymentOptions,
    IStartBookingPaymentResult
} from "./payment-handler.view-model.interface";
import {ValidationResultEnum} from "../../../../types/validation-result.enum";
import {TimeSpan} from "../../../../types/time-span";
import {IBeginPaymentStrategyOption, IPaymentStrategy} from "./strategies/payment-strategy.interface";
import {BookingSessionStorageKeys} from "../storage/booking-session-storage-keys";
import {PaymentStatusObserver} from "./payment-status-observer";
import {PaymentResultsDialogFactory} from "./payment-results-dialog-factory";
import {PaymentStatusQueryParamEnum, PaymentStatusQueryParamValuesEnum} from "./payment-status-query-params.enum";
import {PaymentTransactionStatusEnum} from "../../../airline-webapi/enums/payment-transaction-status.enum";
import {delay} from "../../../../utils/util-functions";
import {Check} from "../../../../types/type-checking";
import {WebapiPaymentStrategy} from "./strategies/webapi-payment.strategy";
import {IPaymentStatusResponse} from "../../../airline-webapi/responses/payment-status.response";
import {IPaymentMethodViewModel} from "./methods/payment-method.view-model.interface";
import {NullableString} from "../../../../types/nullable-types";
import {OrganizationCreditPaymentMethod} from "./methods/organization-credit/organization-credit-payment-method.model";
import {IBookingAuxiliaryPayment} from "./methods/booking-auxiliary-payment.interface";
import {Lazy} from "../../../../utils/lazy";
import {VoucherModel} from "../promotions/voucher/voucher.model";
import {IPaymentBrowserDetails} from "../../../airline-webapi/requests/begin-booking-payment.request";
import {PayInIframeDialogComponent} from "../../../../pages/booking-flow/payment/pay-in-iframe-dialog.component";
import {DialogCloseButtonBehavior} from "../../../dialog/dialog-enums";
import {CardMethodModel} from "./methods/card/card-method.model";
import {PaymentMethodCodesEnum} from "./methods/payment-method-codes.enum";
import React from "react";
import {
    SaveBookingButtonComponent
} from "../../../../pages/booking-flow/payment/save-booking-button/save-booking-button.component";
import {IPaymentMethodModel} from "./methods/payment-method.model.interface";
import {
    PayBookingButtonWrapperComponent
} from "../../../../pages/booking-flow/payment/pay-booking-button-wrapper/pay-booking-button-wrapper.component";

export class PaymentHandlerModel implements IPaymentHandlerViewModel /*, IRoutingGuard*/ {

    constructor(private readonly booking: BookingModel) {

        this._selectedPaymentMethodCode = this.booking.storage.getItem(BookingSessionStorageKeys.selectedPaymentMethod);
        this._holdBooking = this.booking.storage.getItem(BookingSessionStorageKeys.holdBooking) === "true";

        makeObservable<this, '_allPaymentMethods' | '_selectedPaymentMethodCode' | '_holdBooking'>(this, {
            _selectedPaymentMethodCode: observable.ref,
            _allPaymentMethods: observable.ref,
            _holdBooking: observable.ref
        });

        this._paymentStrategy = new WebapiPaymentStrategy(booking);
        this.organizationCredit = new OrganizationCreditPaymentMethod(booking);
        this.resultDialogs = new PaymentResultsDialogFactory(this.booking);



        //_initPaymentMethodsOnPaymentHandlerCreation call is necessary here for the case when the user refresh the page while in the payment page.
        this._initPaymentMethodsOnPaymentHandlerCreation().finally(() => {
            if(this._shouldFinalizePayment) {
                return this._finalizePayment(this.services.navigator.getQueryParamsValues(PaymentStatusQueryParamEnum.Status)[PaymentStatusQueryParamEnum.Status] as PaymentStatusQueryParamValuesEnum);
            } else if(this._shouldTryFinalizePendingPayment) {
                return this._tryFinalizePendingPayment()
            }
        })


        //this._routingGuardSubscription = this.services.navigator.registerRoutingGuard(this);
    }

    private readonly _paymentStrategy: IPaymentStrategy;
    readonly resultDialogs: PaymentResultsDialogFactory;
    //private readonly _routingGuardSubscription: IRoutingGuardSubscription;
    private _selectedPaymentMethodCode: NullableString = null;
    private _preventDoublePayBooking = false;
    private _activateFromPaymentReaction: IReactionDisposer | null = null;
    private _redirectPromiseResolver: null | ((value: void | PromiseLike<void>) => void) = null;
    public readonly organizationCredit: OrganizationCreditPaymentMethod;

    private _holdBooking: boolean = false;
    get holdBooking(): boolean {
        return this._holdBooking;
    }

    set holdBooking(value: boolean) {
        runInAction(() => {
            this._holdBooking = value;
            this.booking.storage.setItem(BookingSessionStorageKeys.holdBooking, this._holdBooking.toString());
        });

    }

    get paymentMethodsDisabled(): boolean {
        return this.booking.balanceDue.amount <= 0 || this.booking.paymentHandler.holdBooking;
    }

    private readonly _voucherModel: Lazy<VoucherModel> = new Lazy<VoucherModel>(() => new VoucherModel(this.booking));
    get voucher(): VoucherModel {
        return this._voucherModel.value;
    }


    dispose(): void {
        //this._routingGuardSubscription.unsubscribe();
    }

    private get services(): IServiceFactory {
        return this.booking.services;
    }

    private get session(): IDotRezBookingSession {
        return this.booking.session;
    }

    private _allPaymentMethods: IPaymentMethodModel[] = [];
    get allPaymentMethods(): IPaymentMethodModel[] {
        return this._allPaymentMethods;
    }


    private _findPaymentMethodByInstance<TMethod>(instanceConstructor: new (...args: any[]) => TMethod): TMethod | null {
        const method = this.allPaymentMethods.find(pm => pm instanceof instanceConstructor);
        if(method) {
            return method as TMethod;
        }
        return null;
    }

    get cardPaymentMethod(): CardMethodModel {
        return this._findPaymentMethodByInstance(CardMethodModel)!;
    }



    async loadPaymentMethods(options: ILoadPaymentMethodsOptions): Promise<void> {
        await this._paymentStrategy.loadPaymentMethods(options);
        if(options.showLoadingIndicator) {
            await this.services.loadingIndicator.execute({
                action: async () => {
                    await this._initPaymentMethods();
                }
            })
        } else {
            await this._initPaymentMethods();
        }

    }

    private async _initPaymentMethodsOnPaymentHandlerCreation(): Promise<void> {
        if(!this.booking.bookingStrategyAdapter.supportsPayment) {
            return;
        }

        if(this.booking.bookingStrategyAdapter.getPaymentRoute().equals(this.services.navigator.currentRoute)) {
            await this._initPaymentMethods();
        }
    }
    private async _initPaymentMethods(): Promise<void> {
        const availablePaymentMethods: IPaymentMethodModel[] = [];
        for(let pm of this._paymentStrategy.allPaymentMethods) {
            if(await pm.canMakePayments()) {
                availablePaymentMethods.push(pm);
            }
        }

        await this.organizationCredit.loadOrganizationCredit();

        runInAction(() => {
            this._allPaymentMethods = availablePaymentMethods;
            if(!this._selectedPaymentMethodCode) {
                this._selectedPaymentMethodCode = availablePaymentMethods[0]?.code;
            }
        });
    }

    selectPaymentMethod(code: PaymentMethodCodesEnum): void {

        runInAction(() => {
            let selectedCode  = this.allPaymentMethods.find(m => m.code === code)?.code;
            if(!selectedCode) {
                selectedCode = this.allPaymentMethods[0]?.code;
            }

            this.booking.storage.setItem(BookingSessionStorageKeys.selectedPaymentMethod, selectedCode);
            this._selectedPaymentMethodCode = selectedCode;

        })
    }

    getSelectedPaymentMethod(): IPaymentMethodModel | null {
        return this.allPaymentMethods.find(pm => pm.code === this._selectedPaymentMethodCode) ?? null;

    }

    renderPayButton(): React.ReactElement {
        if(this.paymentMethodsDisabled) {
            return (<SaveBookingButtonComponent/>);
        }

        return (
            <PayBookingButtonWrapperComponent renderPayButton={() => this.getSelectedPaymentMethod()?.renderPayButton() ?? null}/>
        );

    }

    private async _refreshBookingData(): Promise<void> {
        const bookingData = await this.session.bookingStateQueryBuilder().useBookingData().getBookingState();
        this.booking.updateBookingSessionData(bookingData);
    }

    private async _executePostSaveBookingOperations(): Promise<void> {
        if (this.voucher.isApplied) {
            this.voucher.clear();
        }

        this.booking.saveToMyTrips();

        this.services.analytics.paymentEvents.logPurchase(this.booking)

        try {
            if(this.booking.getAllFilteredPassengersSegments().some(p => p.isCheckedIn)) {
                await this.services.airlineWebapi.updateBoardingPassesInWallets({
                    dotRezToken: this.session.token
                })
            }
        } catch (err) {
            this.services.logger.error(`Failed to update boarding passes in wallets for PNR ${this.booking.recordLocator}`, err);
        }

        //await this.booking.moveBetweenDisruptionQueues();
        await this.booking.removeFromDisruptionQueues();


        this.services.flightSearch.resetFields();
    }


    get auxiliaryPayments(): IBookingAuxiliaryPayment[] {
        return [
            this.organizationCredit,
            this.voucher
        ];
    }

    get totalAuxiliaryPaymentsAmount(): number {
        return this.auxiliaryPayments.sum(ap => ap.usedAmount);
    }


    private async _applyAuxiliaryPayments(): Promise<{errorMessage: NullableString; paymentsKeys: string[]}> {


        const paymentsKeys: string[] = [];
        for(let auxiliaryPayment of this.auxiliaryPayments) {
            const payResult = await auxiliaryPayment.addToBooking();
            if(payResult.errorMessage) {
                return {
                    errorMessage: payResult.errorMessage,
                    paymentsKeys: paymentsKeys
                }
            } else if(payResult.paymentKey) {
                paymentsKeys.push(payResult.paymentKey);
            }
        }

        return {
            errorMessage: null,
            paymentsKeys: paymentsKeys
        };
    }

    private async _rollbackAuxiliaryPayments(paymentsKeys: string[]): Promise<ValidationResultEnum> {
        if(paymentsKeys.length === 0) {
            return ValidationResultEnum.Success;
        }

        let result = ValidationResultEnum.Success;
        for(let paymentKey of paymentsKeys) {
            try {
                await this.booking.session.removePayment(paymentKey);
            } catch (err) {
                this.services.logger.error(`Failed to rollback payment ${paymentKey}`, err);
                result = ValidationResultEnum.Failure;
            }
        }

        return result;
    }

    private async _saveZeroBalanceDueBooking(): Promise<IStartBookingPaymentResult> {
        const hasAuxiliaryPayments = this.totalAuxiliaryPaymentsAmount > 0;
        const transactionResult = await this.services.loadingIndicator.execute({
            action: async () => {
                const auxiliaryPaymentsResult = await this._applyAuxiliaryPayments();
                if(auxiliaryPaymentsResult.errorMessage) {

                    const rollbackResult = await this._rollbackAuxiliaryPayments(auxiliaryPaymentsResult.paymentsKeys);
                    if(rollbackResult !== ValidationResultEnum.Success) {
                        this.resultDialogs.showPaymentFatalError({
                            pspErrorDetails: []
                        });
                        return PaymentTransactionStatusEnum.FatalError;
                    }

                    this.services.alert.showError(auxiliaryPaymentsResult.errorMessage);

                    await this._refreshBookingData();

                    return PaymentTransactionStatusEnum.Error;
                }

                try {
                    await this.booking.commitBooking({notifyContacts: true});
                } catch (err) {
                    this.services.logger.error('Payment handler failed to commit booking', err);
                    const rollbackResult = await this._rollbackAuxiliaryPayments(auxiliaryPaymentsResult.paymentsKeys);
                    if(rollbackResult !== ValidationResultEnum.Success) {
                        this.resultDialogs.showPaymentFatalError({
                            pspErrorDetails: []
                        });
                        return PaymentTransactionStatusEnum.FatalError;
                    }

                    this.services.alert.showError(this.services.language.translate('There was an error saving your booking. Please try again latter.'));
                    return PaymentTransactionStatusEnum.Error;
                }

                await this._refreshBookingData();

                await this._executePostSaveBookingOperations();
                return PaymentTransactionStatusEnum.Completed;
            }
        });

        if(transactionResult !== PaymentTransactionStatusEnum.Completed) {
            return {
                transactionStatus: transactionResult,
                pspErrorDetails: [],
                pspOrderToken: null,
                apiErrorCode: null,
                redirectUrl: null
            }
        } else {
            await this.booking.bookingStrategyAdapter.onPaymentSuccess();
            if(hasAuxiliaryPayments) {
                this.resultDialogs.showPaymentSuccess();
            } else {
                this.resultDialogs.showPaymentSuccess({customMessage: this.services.language.translate('Booking saved!')});
            }

            return {
                transactionStatus: PaymentTransactionStatusEnum.Completed,
                pspErrorDetails: [],
                pspOrderToken: null,
                apiErrorCode: null,
                redirectUrl: null
            }
        }
    }

    private async _savePayedBooking(): Promise<void> {
        try {
            await this.booking.commitBooking({notifyContacts: false});
            await this._refreshBookingData();
        } catch (err) {
            this.services.logger.error('Payment handler failed to save payed booking', err);
        } finally {
            //If the booking is paid it doesn't matter the outcome of the commitBooking and _refreshBookingData.
            //We need to give the user the ticket because the booking is paid
            await this._executePostSaveBookingOperations();
        }
    }

    private async _onPayBookingSuccess(): Promise<void> {
        this.paymentAttempt++;
        await this.services.loadingIndicator.execute({
            action: async () => {
                await this._savePayedBooking();
            }
        });

        this.services.analytics.paymentEvents.logAddRemarketingPurchase(this.booking);
        this._paymentStrategy.onPaymentFinalized();
        await this.booking.bookingStrategyAdapter.onPaymentSuccess();
        this.resultDialogs.showPaymentSuccess();


    }



    async onPayBookingFailed(options: IOnPayBookingFailedParams): Promise<void> {

        try {
            await this.services.loadingIndicator.execute({
                action: async () => {
                    if(options.shouldRefreshBooking) {
                        await this._refreshBookingData();
                    }
                    await this.loadPaymentMethods({showLoadingIndicator: false});
                }
            })
        } catch (err) {
            this.services.logger.error('Failed to refresh booking and reload payment methods after failed payment', err);
        }

        if(options.status === PaymentTransactionStatusEnum.FatalError) {
            this.resultDialogs.showPaymentFatalError({
                pspErrorDetails: options.pspErrorDetails
            });
            return;
        }

        this.paymentAttempt++;
        if(options.apiErrorCode && this.booking.errorHandling.canHandleApiError(options.apiErrorCode)) {
            this._paymentStrategy.onPaymentFinalized();
            await this.booking.errorHandling.handleApiError(options.apiErrorCode);
        } else {
            switch (options.status) {
                case PaymentTransactionStatusEnum.Cancel:
                    this._paymentStrategy.onPaymentFinalized();
                    //this.resultDialogs.showPaymentCanceled();
                    break;
                default:
                    this._paymentStrategy.onPaymentFinalized();
                    if(!options.dontShowErrorMessage) {
                        this.resultDialogs.showPaymentFailed({
                            errorMessage: null,
                            pspErrorDetails: options.pspErrorDetails
                        });
                    }

                    break;
            }
        }
    }

    private async _tryRefreshToken(): Promise<ValidationResultEnum> {
        try {
            const result = await this.booking.session.tryRefreshSession();
            if(result.isExpired) {
                return ValidationResultEnum.Failure;
            } else {
                return ValidationResultEnum.Success;
            }

        } catch (err) {
            this.services.alert.showError(this.services.language.translate('There was an error trying to initialize your payment transaction'));
            return ValidationResultEnum.Failure;
        }
    }

    async performStartBookingPaymentValidations(): Promise<{result: ValidationResultEnum; errorMessage?: string}> {
        if (this.booking.balanceDue.amount < 0) {
            this.services.logger.error('Negative booking cannot be payed');
            const msg = this.services.language.translate('Booking with negative balance due cannot be saved');
            this.services.alert.showError(msg);
            return {
                result: ValidationResultEnum.Failure,
                errorMessage: msg
            }
        }

        const insuranceErrorValidationMessage = this.booking.insurance.insuranceInPaymentPageStrategy?.validate();

        if(insuranceErrorValidationMessage) {
            //this.services.alert.showError(insuranceErrorValidationMessage);
            return {
                result: ValidationResultEnum.Failure,
                errorMessage: insuranceErrorValidationMessage
            }
        }

        if(this.booking.validateTravelConditionsAcceptance() !== ValidationResultEnum.Success) {
            return {
                result: ValidationResultEnum.Failure,
                errorMessage: this.booking.travelConditionsAcceptanceError!
            }
        }

        if(this.booking.balanceDue.amount > 0) {
            if(this.booking.invoice.invoiceRequested) {
                if(ValidationResultEnum.Success !== await this.booking.invoice.validate()) {
                    const msg = this.services.language.translate('Invalid invoice fields values');
                    this.services.alert.showError(msg);
                    return {
                        result: ValidationResultEnum.Failure,
                        errorMessage: msg
                    }
                }
            }
        }

        return {
            result: ValidationResultEnum.Success
        };
    }

    private async _putBookingInHold(): Promise<boolean> {
        if(!this.holdBooking) {
            return false;
        }

        await this.services.loadingIndicator.execute({
            action: async () => {
                const holdExpiration = this.services.time.formatUserFriendlyDateAndTime(await this.booking.putInHold());
                await this._refreshBookingData();
                await this.booking.bookingStrategyAdapter.onPaymentSuccess();
                this.resultDialogs.showPaymentSuccess({
                    customMessage: this.services.language.translationFor(`Your booking is on hold until <br/>{holdExpiration}`).withParams({holdExpiration: holdExpiration}),
                    showOnlyCustomMessage: true
                });
                this.services.flightSearch.resetFields();
            }
        });

        return true;

    }

    async startBookingPayment(options?: IStartBookingPaymentOptions): Promise<IStartBookingPaymentResult> {
        if(this._preventDoublePayBooking) {
            return this._canceledPayBookingResponse();
        }

        this._preventDoublePayBooking = true;

        try {


            if(options?.paymentMethodCode) {
                this.selectPaymentMethod(options.paymentMethodCode);
            }

            if(!options?.skipValidations) {
                const validationsResult = await this.performStartBookingPaymentValidations();
                if(validationsResult.result !== ValidationResultEnum.Success) {
                    return this._errorPayBookingResponse(validationsResult.errorMessage ?? "");
                }
            }

            if(await this._putBookingInHold()) {
                return {
                    transactionStatus: PaymentTransactionStatusEnum.Completed,
                    pspErrorDetails: [],
                    pspOrderToken: null,
                    apiErrorCode: null,
                    redirectUrl: null
                }
            }

            if(this.booking.balanceDue.amount === 0) {
                return await this._saveZeroBalanceDueBooking();
            }

            const refreshTokenResult = await this.services.loadingIndicator.execute({
                action: async () => {
                    return this._tryRefreshToken();
                }
            });

            if(refreshTokenResult !== ValidationResultEnum.Success) {
                return this._errorPayBookingResponse(this.services.language.translate('Your session has expired'));
            }

            const selectedPaymentMethod =  this.getSelectedPaymentMethod();
            if(!selectedPaymentMethod) {
                const msg = this.services.language.translate('Please select a payment method');
                this.services.alert.showError(msg);
                return this._errorPayBookingResponse(msg);
            }

            this.services.analytics.paymentEvents.logAddPaymentInfo(this.booking);

            try {
                return await this._beginPayment(selectedPaymentMethod, options);
            } catch (err) {
                this.services.logger.error('_beginPayment failed', err);
                await this.onPayBookingFailed({
                    status: PaymentTransactionStatusEnum.Error,
                    shouldRefreshBooking: true,
                    apiErrorCode: null,
                    pspErrorDetails: null
                });
                return this._errorPayBookingResponse(this.getGenericPaymentError());
            }
        } finally {
            this._preventDoublePayBooking = false;
        }
    }


    getGenericPaymentError(): string {
        return this.booking.errorHandling.getGenericPaymentError();
    }


    async finalizePspOrder(request?: IFinalizePspOrderRequest): Promise<void> {
        try {

            const response = await this.services.loadingIndicator.execute({
                action: async () => {
                    return await this._paymentStrategy.finalizePspOrder();
                }
            });

            if(response.paymentStatus === PaymentTransactionStatusEnum.Completed) {
                await this._onPayBookingSuccess();
                return;
            }

            await this.services.loadingIndicator.execute({
                action: async () => {
                    await this.booking.session.resumeSessionTimer();
                }
            });


            if(await this.booking.session.isExpired()) {
                return;
            }

            switch (response.paymentStatus) {
                case PaymentTransactionStatusEnum.PendingReview:
                    this.resultDialogs.showPaymentInReview()
                    break;
                default:
                    await this.onPayBookingFailed({
                        status: response.paymentStatus,
                        shouldRefreshBooking: true,
                        apiErrorCode: response.apiErrorCode,
                        pspErrorDetails: response.pspErrorDetails,
                        dontShowErrorMessage: request?.dontShowErrorMessage
                    });

                    break;
            }

        } catch (err) {
            this.services.logger.error('finalizeMobileWalletPayment failed!', err);
            await this.onPayBookingFailed({
                status: PaymentTransactionStatusEnum.Error,
                shouldRefreshBooking: true,
                apiErrorCode: null,
                pspErrorDetails: null
            });
        }
    }

    async cancelPspOrder(): Promise<void> {

        this._paymentStrategy.cancelPspOrder(); //no need to wait for the promise;

        await this.booking.session.resumeSessionTimer();

        if(!await this.booking.session.isExpired()) {
            await this.onPayBookingFailed({
                status: PaymentTransactionStatusEnum.Cancel,
                shouldRefreshBooking: false,
                apiErrorCode: null,
                pspErrorDetails: null
            });
        }
    }



    private _canceledPayBookingResponse(): IStartBookingPaymentResult {
        return {
            transactionStatus: PaymentTransactionStatusEnum.Cancel,
            pspOrderToken: null,
            apiErrorCode: null,
            redirectUrl: null,
            pspErrorDetails: []
        }
    }

    private _errorPayBookingResponse(message: string): IStartBookingPaymentResult {
        return {
            transactionStatus: PaymentTransactionStatusEnum.Error,
            pspOrderToken: null,
            redirectUrl: null,
            apiErrorCode: null,
            pspErrorDetails: [
                {
                    code: "1",
                    message: message
                }
            ]
        }
    }


    private get paymentAttempt(): number {
        const attempt = this.booking.storage.getItem(BookingSessionStorageKeys.paymentAttempt);
        if(attempt) {
            return parseInt(attempt);
        }

        return 1;
    }

    private set paymentAttempt(value: number) {
        this.booking.storage.setItem(BookingSessionStorageKeys.paymentAttempt, value.toString())
    }

    private async _beginPayment(selectedMethod: IPaymentMethodViewModel, options?: IStartBookingPaymentOptions): Promise<IStartBookingPaymentResult> {


        const startBookingResult = await this.services.loadingIndicator.execute({
            action: async () => {
                const beginPaymentRequest: IBeginPaymentStrategyOption = {
                    selectedMethod: selectedMethod,
                    paymentAttempt: this.paymentAttempt,
                    browserDetails: this._collectBrowserDetails(),
                    useIframeForRedirect: Boolean(options?.useIframeForRedirect)
                }
                if(options?.mobileWalletToken) {
                    beginPaymentRequest.mobileWalletToken = options.mobileWalletToken;
                    beginPaymentRequest.mobileWalletProvider = options.mobileWalletProvider;
                }

                if(options?.cardDetails) {
                    beginPaymentRequest.cardDetails = options.cardDetails;
                }

                return await this._paymentStrategy.beginPayment(beginPaymentRequest);
            }
        });




        switch (startBookingResult.transactionStatus) {
            case PaymentTransactionStatusEnum.Error:
            case PaymentTransactionStatusEnum.FatalError:
            case PaymentTransactionStatusEnum.Cancel:
                await this.onPayBookingFailed({
                    status: startBookingResult.transactionStatus,
                    apiErrorCode: startBookingResult.apiErrorCode,
                    shouldRefreshBooking: true,
                    pspErrorDetails: startBookingResult.pspErrorDetails
                });
                break;

            case PaymentTransactionStatusEnum.Completed:
                await this._onPayBookingSuccess();
                break;

            case PaymentTransactionStatusEnum.NotFinalized:
                this.booking.session.pauseSessionTimer();
                if(startBookingResult.redirectUrl) {
                    if(options?.useIframeForRedirect) {
                        await this._payInIframe(startBookingResult)
                    } else {
                        await this._payRedirect(startBookingResult);
                    }

                }
               break;

            default:
                throw new Error(`Invalid begin payment status ${startBookingResult.transactionStatus}`);
        }

        return startBookingResult;
    }

    private _collectBrowserDetails(): IPaymentBrowserDetails {
        return {
            colorDepth: window.screen.colorDepth,
            javaEnabled: Boolean(navigator.javaEnabled && navigator.javaEnabled()),
            language: this.services.language.currentLanguage,
            screenHeight: window.screen.height,
            screenWidth: window.screen.width,
            timeZoneOffset: this.services.time.getTimeZoneOffset(),
            userAgent: navigator.userAgent
        }
    }

    private _getPaymentStatusFromUrl(url: string): PaymentStatusQueryParamValuesEnum | null {
        url = url?.toString()?.toLowerCase();
        if(!url) {
            return null;
        }


        if(0 <= url.indexOf('mobileapp/finalize')) {
            const urlBuilder = new URL(url);
            const callBackTypeParamValue = urlBuilder.searchParams.get('callbacktype')?.toString();
            switch (callBackTypeParamValue) {
                case '0':
                    return PaymentStatusQueryParamValuesEnum.Success;
                case '1':
                    return PaymentStatusQueryParamValuesEnum.Canceled;
                default:
                    return PaymentStatusQueryParamValuesEnum.Failed;
            }
        }


        return null;
    }



    private _disposeActivateFromPaymentReaction(): void {
        if(this._activateFromPaymentReaction) {
            this._activateFromPaymentReaction();
            this._activateFromPaymentReaction = null;
        }
    }

    private _resolveRedirectPromise = () => {
        if(this._redirectPromiseResolver) {
            this._redirectPromiseResolver();
            this._redirectPromiseResolver = null;
        }
    }
    private async _payInIframe(startBookingResult: IStartBookingPaymentResult): Promise<void> {
        if(!startBookingResult.redirectUrl) {
            this.services.logger.error(`Missing payment redirect URL`);
            await this.onPayBookingFailed({
                status: PaymentTransactionStatusEnum.Error,
                shouldRefreshBooking: true,
                apiErrorCode: null,
                pspErrorDetails: null
            });
            return;
        }

        await this.services.dialog.showStandardDialog<PaymentStatusQueryParamValuesEnum>({
            closeButtonBehavior: DialogCloseButtonBehavior.Reject,
            render: dialogHandler => (<PayInIframeDialogComponent url={startBookingResult.redirectUrl ?? ""} dialogHandler={dialogHandler}/>),
            onAccept: async () =>  {
                await this._tryFinalizePendingPayment({noTimeout: true})
            },
            onReject: async () => {
                await this._tryFinalizePendingPayment();
            }
        });

    }

    private async _payRedirect(startBookingResult: IStartBookingPaymentResult): Promise<void> {
        if(!startBookingResult.redirectUrl) {
            this.services.logger.error(`Missing payment redirect URL`);
            await this.onPayBookingFailed({
                status: PaymentTransactionStatusEnum.Error,
                shouldRefreshBooking: true,
                apiErrorCode: null,
                pspErrorDetails: null
            });
            return;
        }

        if(this.services.device.isHybrid) {
            await this._payRedirectWithAdvancedInAppBrowser(startBookingResult.redirectUrl!);
        } else {

            await this._payRedirectWithNavigation(startBookingResult.redirectUrl!)
        }
    }

    private async _payRedirectWithAdvancedInAppBrowser(redirectUrl: string): Promise<void> {


        let lastUrl = "";

        const advancedInAppBrowser = await this.services.loadingIndicator.execute({
            action: async () => {
                return await this.services.window.openAdvancedInAppBrowser({
                    url: redirectUrl,
                    theme: this.services.theme.currentTheme,
                    enableDebug: !this.services.configuration.isProduction,
                    urlPatternsToOpenInExternalBrowser: [
                        '/cookies-policy',
                        '/privacy-notice',
                        '/terms-portal',
                        '/privacy',
                        '/terms'
                    ],
                    onUrlChanged: async (url) => {
                        lastUrl = url;
                        //this.services.logger.error('urlChanged: ' + url);
                        if(!Check.isNullOrUndefined(this._getPaymentStatusFromUrl(url))) {
                            await advancedInAppBrowser?.close();
                        }
                    }
                });
            }
        });

        this.booking.session.pauseSessionTimer();

        await advancedInAppBrowser.wait();

        await this.booking.session.resumeSessionTimer();


        const paymentStatusFromUrl = this._getPaymentStatusFromUrl(lastUrl);
        if(Check.isNullOrUndefined(paymentStatusFromUrl)) {
            await this._tryFinalizePendingPayment();
        } else {
            await this._finalizePayment(paymentStatusFromUrl);
        }

    }

    private async _payRedirectWithNavigation(redirectUrl: string): Promise<void> {
        this.booking.session.pauseSessionTimer();
        //This reaction here is because in production after redirect from payment page back to the website
        //the browser might have cached our payment page so instead of a full page load it just activates the page
        this._activateFromPaymentReaction = reaction(() => this.services.application.isActive,
            async (isActive) => {
                if(isActive) {

                    await this.booking.session.resumeSessionTimer();

                    if(this._shouldTryFinalizePendingPayment) {
                        await this._tryFinalizePendingPayment();
                    }

                    this._disposeActivateFromPaymentReaction();
                    this._resolveRedirectPromise();
                }
            });

        const resultPromise =  new Promise<void>((resolve) => {
            this._redirectPromiseResolver = resolve;
        });

        await this.services.loadingIndicator.execute({
            action: async () => {
                this.services.navigator.redirect(redirectUrl);
                await delay(Date.now(), 10000); // To keep the spinner running until the redirect actually happen.
            }
        })


        return resultPromise;
    }

    private get isRedirectInProgress(): boolean {
        return this._paymentStrategy.isRedirectInProgress;
    }

    private get _shouldFinalizePayment(): boolean {
        return this.booking.bookingStrategyAdapter.supportsPayment
            && this.isRedirectInProgress
            && this.booking.bookingStrategyAdapter.getFinalizePaymentRoute().equals(this.services.navigator.currentRoute);
    }

    private get _shouldTryFinalizePendingPayment(): boolean {
        return this.booking.bookingStrategyAdapter.supportsPayment
            && this.isRedirectInProgress
            && this.booking.bookingStrategyAdapter.getPaymentRoute().equals(this.services.navigator.currentRoute);
    }

    private async _finalizePayment(paymentStatusFromQueryParams: PaymentStatusQueryParamValuesEnum): Promise<void> {

        this._disposeActivateFromPaymentReaction();

        this._resolveRedirectPromise();

        const onPaymentCanceled = async (paymentStatusResponse: IPaymentStatusResponse) => {

            this.booking.bookingStrategyAdapter.getPaymentRoute().activate({
                allowBack: false
            });

            await this.onPayBookingFailed({
                status: PaymentTransactionStatusEnum.Cancel,
                shouldRefreshBooking: false,
                apiErrorCode: paymentStatusResponse.apiErrorCode,
                pspErrorDetails: paymentStatusResponse.pspErrorDetails
            });
        }

        const onPaymentFailed = async (paymentStatusResponse: IPaymentStatusResponse) => {
            this.booking.bookingStrategyAdapter.getPaymentRoute().activate({
                allowBack: false
            });
            await this.onPayBookingFailed({
                status: PaymentTransactionStatusEnum.Error,
                shouldRefreshBooking: false,
                apiErrorCode: paymentStatusResponse.apiErrorCode,
                pspErrorDetails: paymentStatusResponse.pspErrorDetails
            });
        }

        const onPaymentFailedFatal = async (paymentStatusResponse: IPaymentStatusResponse) => {
            await this.onPayBookingFailed({
                status: PaymentTransactionStatusEnum.FatalError,
                shouldRefreshBooking: false,
                apiErrorCode: null,
                pspErrorDetails: paymentStatusResponse.pspErrorDetails
            });
        }



        const paymentStatus = await this.services.loadingIndicator.execute({
            action: async () => {
                const paymentStatusObserver = new PaymentStatusObserver(this.services, this._paymentStrategy);
                let timeout: TimeSpan | undefined = undefined;
                //if the status from query params is success then we will wait until we get a finalized status
                //This is because when we get success callback the status in apcopay can be OK or PENDING. So we need to wait for a final state
                if(paymentStatusFromQueryParams !== PaymentStatusQueryParamValuesEnum.Success) {
                    timeout = TimeSpan.fromSeconds(3);
                }
                return await paymentStatusObserver.start(timeout);
            }
        });

        if(paymentStatus.status === PaymentTransactionStatusEnum.Completed) {
            await this._onPayBookingSuccess();
            return;
        }

        if(await this.booking.session.isExpired()) {
            return;
        }

        switch (paymentStatus.status) {

            case PaymentTransactionStatusEnum.Cancel:
                await onPaymentCanceled(paymentStatus);
                break;
            case PaymentTransactionStatusEnum.FatalError:
                await onPaymentFailedFatal(paymentStatus);
                break;
            default:
                if(paymentStatusFromQueryParams === PaymentStatusQueryParamValuesEnum.Canceled) {
                    await onPaymentCanceled(paymentStatus);
                } else {
                    await onPaymentFailed(paymentStatus);
                }

        }
    }

    private async _tryFinalizePendingPayment(options?: {noTimeout?: boolean}): Promise<void> {

        //we end-up here when the user press back button from payment provider page
        //so in this case we try to get a final status for 3 seconds.
        const paymentStatus = await this.services.loadingIndicator.execute({
            action: async () => {
                const paymentStatusObserver = new PaymentStatusObserver(this.services, this._paymentStrategy);
                if(options?.noTimeout) {
                    return await paymentStatusObserver.start();
                } else {
                    return await paymentStatusObserver.start(TimeSpan.fromSeconds(4));
                }
            }
        });


        if(paymentStatus.status === PaymentTransactionStatusEnum.NotFinalized) {
            await this.loadPaymentMethods({
                showLoadingIndicator: true
            });
        } else if(paymentStatus.status === PaymentTransactionStatusEnum.Completed) {
            await this._onPayBookingSuccess();
        } else {

            if(!await this.booking.session.isExpired()) {

                await this.onPayBookingFailed({
                    status: paymentStatus.status,
                    shouldRefreshBooking: false,
                    apiErrorCode: paymentStatus.apiErrorCode,
                    pspErrorDetails: paymentStatus.pspErrorDetails
                });
            }


        }
    }
}


